import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import axios from 'axios';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {
    addFromColumn,
    convertStartEndDatesForFile,
    sortCurrency,
    toCurrency,
} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import DialogSalesTaxAuditDetail from './DialogSalesTaxAuditDetail';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/action/receipt';
import Config from '../messages';

/**
 * Class for retrieving and displaying Sales Tax Audit Summary in a single table
 *
 */
class SalesTaxAuditReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Store', key: 'location_id'},
                {label: 'State', key: 'state'},
                {label: 'DBA', key: 'dba_name'},
                {label: 'County', key: 'county'},
                {label: 'City Limits', key: 'city_limits'},
                {label: 'City', key: 'city'},
                {label: 'Tax Rate', key: 'tax_rate'},
                {label: 'Gross Transactions', key: 'transactionsGross'},
                {label: 'Sales Tax Collected', key: 'salesTax'},
            ],
            message: null,
            reportName: 'Sales Tax Audit',
            summaryComplete: false,
            taxDetail: null,
            taxDetailOpen: false,
            totals: {
                transactionsGross: 0,
                salesTax: 0
            }
        };
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.fetchData();
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.offset !== this.props.offset
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                summaryList: [],
                summaryComplete: false,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.offset !== this.props.offset
        ) {
            this.fetchData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get Sales data for a specific date range
     *
     */
    fetchData() {
        // Assemble the URI
        const params = {
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset,
        };
        const resource = reportsResources.SalesTaxAudit.GET_LIST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction/type results
     *
     * @param data
     */
    processData(data) {

        const emptyCurrency = toCurrency(0);
        let summaryList = [];
        let totals = {};

        if (data.aggregations) {
            if (data.aggregations.by_state && data.aggregations.by_state.buckets) {
                let results = data.aggregations.by_state.buckets;
                let resultsTotal = results.length;
                if (resultsTotal) {
                    // Assemble data for each state key
                    for (let s = 0; s < resultsTotal; s++) {
                        let stateResult = results[s];
                        let key = stateResult.key;
                        // Assemble data for each location in this state
                        let locations = stateResult.by_location.buckets;
                        let locationsTotal = locations.length;
                        if (locationsTotal) {
                            for (let i = 0; i < locationsTotal; i++) {
                                let location = null;
                                let locationResult = locations[i];
                                if (locationResult) {
                                    location = locationResult.column_name.hits.hits[0]._source.rxtxMsg.payload.location;
                                    // Add rates and totals from Taxes and Additional Taxes
                                    let taxRate = location.tax_rate;
                                    let additionalTaxRates = [];
                                    let locationAdditionalTaxes = locationResult.location_agg_additional_taxes.by_additional_tax.buckets;
                                    for (let at = 0; at < locationAdditionalTaxes.length; at++) {
                                        let atObj = locationAdditionalTaxes[at];
                                        let amount = (atObj.additional_tax_amount.value) ? atObj.additional_tax_amount.value : 0;
                                        let amountFormatted = (amount) ? toCurrency(amount / 100) : emptyCurrency;
                                        additionalTaxRates.push({
                                            amount: amount,
                                            amount_formatted: amountFormatted,
                                            name: atObj.column_name.hits.hits[0]._source.name,
                                            tax_rate: (atObj.column_name.hits.hits[0]._source.tax_rate) ? (atObj.column_name.hits.hits[0]._source.tax_rate * 100) : 0
                                        });
                                    }
                                    let totalTaxRate = (taxRate) ? taxRate : 0;
                                    for (let ttr = 0; ttr < additionalTaxRates.length; ttr++) {
                                        totalTaxRate += additionalTaxRates[ttr].tax_rate;
                                    }
                                    let locationTax = locationResult.location_agg_tax.value;
                                    let locationAdditionalTax = locationResult.location_agg_additional_tax.value;
                                    let taxTotal = locationTax + locationAdditionalTax;
                                    let locationSubtotal = locationResult.location_agg_subtotal.value;
                                    let locationGross = locationResult.location_agg_currency.value;
                                    // Create a row for each Location
                                    const locationRow = {
                                        location_id: (location.id) ? location.id : '----',
                                        dba_name: (location.dba && location.dba.name) ? location.dba.name : '(None)',
                                        county: (location.county) ? location.county : '(None)',
                                        city_limits: (location.city_limits) ? 'Inside' : 'Outside',
                                        city: (location.city) ? location.city : '(None)',
                                        state: key,
                                        tax_rate: (taxRate) ? taxRate + '%' : '0%',
                                        total_tax_rate: (totalTaxRate) ? totalTaxRate + '%' : '0%',
                                        transactionsGross: (locationGross) ? (toCurrency(locationGross / 100)) : emptyCurrency,
                                        transactionsSubtotal: (locationSubtotal) ? (toCurrency(locationSubtotal / 100)) : emptyCurrency,
                                        baseTax: (locationTax) ? (toCurrency(locationTax / 100)) : emptyCurrency,
                                        salesTax: (taxTotal) ? (toCurrency(taxTotal / 100)) : emptyCurrency,
                                        additional_taxes: additionalTaxRates
                                    };
                                    summaryList.push(locationRow);
                                }
                            }
                        }
                        // Multiple table structure (unused):
                        // {(state) key, (state) name, locations[]}
                    }
                } else {
                    // No results for selected date range
                }
            } else {
                // Structure does not conform
            }
            // Store totals (unused)
            if (data.aggregations.agg_currency) {
                totals.transactionsGross = data.aggregations.agg_currency;
            }
            if (data.aggregations.agg_tax) {
                totals.salesTax = data.aggregations.agg_tax;
            }
        }

        // // Finalize table data
        this.setState({
            summaryList: summaryList,
            summaryTableData: summaryList,
            summaryComplete: true,
            totals: totals
        });
        this.props.onSummaryComplete(true);
    }

    /* Table Building ------------------------------ */

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    handleDialogOpen = (e, data) => {
        this.setState({
            taxDetail: data,
            taxDetailOpen: true
        });
    };

    handleDialogClose = () => {
        this.setState({
            taxDetail: null,
            taxDetailOpen: false
        });
    };

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            endDate,
            startDate,
            system,
        } = this.props;

        const {
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
            taxDetail,
            taxDetailOpen
        } = this.state;

        const dateString = convertStartEndDatesForFile(startDate, endDate);
        const defaultSort = setDefaultTableSort('location_id');

        // ReactTable headers and columns for Table
        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Store',
                        accessor: 'location_id',
                        style: tableStyles.tdMax
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                        style: tableStyles.tdMax
                    },
                    {
                        Header: 'DBA',
                        accessor: 'dba_name',
                    },
                    {
                        Header: 'County',
                        accessor: 'county',
                    },
                    {
                        Header: 'City Limits',
                        accessor: 'city_limits',
                        style: {...tableStyles.tdMax, ...tableStyles.right}
                    },
                    {
                        Header: 'City',
                        accessor: 'city',
                    },
                    {
                        Header: 'Total Tax Rate',
                        accessor: 'tax_rate',
                        Cell: row => (
                            <span>
                                {row.original.additional_taxes.length > 0 &&
                                    <a style={tableStyles.linkDefault}
                                       onClick={(e) => this.handleDialogOpen(e, row.original)}
                                    >
                                        {row.original.total_tax_rate}
                                    </a>
                                }
                                {row.original.additional_taxes.length === 0 &&
                                    <span>{row.original.total_tax_rate}</span>
                                }
                            </span>
                        ),
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: {...tableStyles.tdMax, ...tableStyles.right}
                    },
                    {
                        Header: 'Gross Transactions',
                        accessor: 'transactionsGross',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        headerStyle: tableStyles.thLast,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(summaryTableData, 'transactionsGross'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Sales Tax Collected',
                        accessor: 'salesTax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        headerStyle: tableStyles.thLast,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(summaryTableData, 'salesTax'))}
                            </span>
                        )
                    }
                ]
            }
        ];

        return (
            <div>
                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Sales Tax Audit */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Sales Tax Audit */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={summaryTableData}
                                        headers={summaryTableHeaders}
                                        filename={'Sales-Tax-Audit-' + system + '-' + dateString + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Sales Tax Audit
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <TableIcon style={commonStyles.summaryIcon}/>
                                <span>Sales Tax Audit</span>
                            </h3>
                        </div>

                        {/* Table: Sales Tax Audit */}
                        <ReactTable
                            key="table-sales-tax-audit"
                            data={summaryList}
                            columns={tableColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                            ref={(r) => this.reactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped summary-table"
                        />
                        <Dialog
                            modal={false}
                            open={taxDetailOpen}
                            onRequestClose={this.handleDialogClose}
                            contentClassName="dialogDetail"
                            autoScrollBodyContent={true}
                            actions={<FlatButton
                                label="Close"
                                primary={true}
                                onClick={this.handleDialogClose}
                            />}
                        >
                            <DialogSalesTaxAuditDetail data={taxDetail}/>
                        </Dialog>
                    </div>
                }
            </div>
        )
    }
}

SalesTaxAuditReport.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
};

SalesTaxAuditReport.defaultProps = {
    addField: true,
    endDate: null,
    offset: null,
    startDate: null,
};

const enhance = compose(
    connect()
);

export default enhance(SalesTaxAuditReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/SalesTaxAuditReport.js